/**
 * c-header.js
 *
 * Behaviour for the `c-header` section.
 *
 * Desktop: nav items open on hover with a short intent delay, the category
 * rail swaps panes on hover and focus, and a scrim dims the page.
 * Category rows with a link are plain anchors, so a click or tap always lands
 * on the collection; hover and focus only swap the pane on desktop.
 * Mobile (<= 1000px): the bar collapses to a burger drawer, each nav item is
 * an accordion, and the panes are hidden by CSS, so the rail is a link list.
 *
 * The 1000px breakpoint is duplicated in `c-header.css`; change both together.
 *
 * Re-initialises on `shopify:section:load` so the theme editor picks up edits,
 * and binds per-root rather than globally so a second instance (a preview in
 * the editor) does not fight the first.
 */
(function () {
  'use strict';

  var MOBILE = '(max-width: 1000px)';
  var OPEN_DELAY = 90;
  var CLOSE_DELAY = 160;

  var mq = window.matchMedia(MOBILE);

  function isMobile() {
    return mq.matches;
  }

  function lockScroll(locked) {
    /* `base.css` drives both the document and `.page-wrapper` — which is the
       real scroll container above 990px — off this attribute. */
    if (locked) {
      document.documentElement.setAttribute('scroll-lock', '');
    } else {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  function init(root) {
    if (!root || root.__cHeaderReady) return;
    root.__cHeaderReady = true;

    var menu = root.querySelector('[data-c-hd-menu]');
    var burger = root.querySelector('[data-c-hd-burger]');
    var scrim = root.querySelector('[data-c-hd-scrim]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-c-hd-item]'));
    var timer = null;

    function anyOpen() {
      return items.some(function (item) {
        return item.classList.contains('is-open');
      });
    }

    function setPanel(target, open) {
      items.forEach(function (item) {
        var on = item === target && open;
        item.classList.toggle('is-open', on);

        var trigger = item.querySelector('[data-c-hd-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', on ? 'true' : 'false');
      });

      if (scrim && !isMobile()) scrim.classList.toggle('is-on', anyOpen());
    }

    function closeAll() {
      setPanel(null, false);
    }

    function closeDrawer() {
      if (!menu) return;
      menu.classList.remove('is-open');
      if (burger) burger.setAttribute('aria-expanded', 'false');
      lockScroll(false);
    }

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-c-hd-trigger]');
      var panel = item.querySelector('[data-c-hd-panel]');
      if (!trigger || !panel) return;

      item.addEventListener('mouseenter', function () {
        if (isMobile()) return;
        clearTimeout(timer);
        /* Once a panel is open, sliding along the bar swaps instantly; the
           first open waits so a pointer crossing the bar does not flash it. */
        timer = setTimeout(
          function () {
            setPanel(item, true);
          },
          anyOpen() ? 0 : OPEN_DELAY
        );
      });

      item.addEventListener('mouseleave', function () {
        if (isMobile()) return;
        clearTimeout(timer);
        timer = setTimeout(function () {
          setPanel(item, false);
        }, CLOSE_DELAY);
      });

      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        clearTimeout(timer);
        setPanel(item, !item.classList.contains('is-open'));
      });

      item.addEventListener('focusout', function (event) {
        if (!isMobile() && !item.contains(event.relatedTarget)) setPanel(item, false);
      });

      /* Category rail: swaps the pane on the right. */
      var tabs = Array.prototype.slice.call(item.querySelectorAll('[data-c-hd-cat]'));
      var panes = Array.prototype.slice.call(item.querySelectorAll('[data-c-hd-pane]'));

      tabs.forEach(function (tab) {
        function show() {
          tabs.forEach(function (other) {
            other.classList.toggle('is-on', other === tab);
          });
          panes.forEach(function (pane) {
            pane.classList.toggle('is-on', pane.getAttribute('data-c-hd-pane') === tab.getAttribute('data-c-hd-cat'));
          });
        }

        tab.addEventListener('mouseenter', function () {
          if (!isMobile()) show();
        });
        tab.addEventListener('focus', show);
        /* A row without a link is a button: its click swaps the pane. */
        if (tab.tagName === 'BUTTON') tab.addEventListener('click', show);
      });
    });

    if (scrim) {
      scrim.addEventListener('mouseenter', closeAll);
      scrim.addEventListener('click', closeAll);
    }

    if (burger && menu) {
      burger.addEventListener('click', function () {
        var open = menu.classList.toggle('is-open');
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        lockScroll(open);
        if (!open) closeAll();
      });
    }

    if (menu) {
      /* Following a link inside the drawer leaves it open behind the next page
         in browsers that restore the DOM, so close it on the way out. */
      menu.addEventListener('click', function (event) {
        if (event.target.closest('a')) {
          closeDrawer();
          closeAll();
        }
      });
    }

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;

      var open = items.filter(function (item) {
        return item.classList.contains('is-open');
      })[0];

      if (open) {
        closeAll();
        var trigger = open.querySelector('[data-c-hd-trigger]');
        if (trigger) trigger.focus();
        return;
      }

      closeDrawer();
    });

    mq.addEventListener('change', function () {
      closeAll();
      closeDrawer();
    });
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-c-hd]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
